/**
 * FeedView Component
 * 
 * Displays the Team Feed with:
 * - Post composer (rich text, images, GIFs, videos, voice memos, polls)
 * - Infinite scroll posts feed
 * - PostCard components for each post
 * 
 * Extracted from TeamCommsApp.js for maintainability.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDesign } from '@/contexts/DesignContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import RichTextEditor from '@/components/shared/RichTextEditor';
import AttachmentMenu from '@/components/shared/AttachmentMenu';
import { getMentionMembers, peekMembers } from '@/lib/mentionDataCache';
import { toast } from 'sonner';
import { 
  Send, 
  Smile, 
  Video, 
  MessageSquare,
  X,
  Loader2,
  BarChart3,
  Play,
  ChevronDown,
  CalendarClock,
  Clock,
  Mail,
  Tag,
  FileText,
  RefreshCw,
  WifiOff,
  MoreHorizontal,
  Image as ImageIcon,
  Users,
} from 'lucide-react';

// Import extracted components
import { 
  VoiceMemoButton, 
  GifPickerDialog, 
  PostCard,
  SchedulePostModal,
  ScheduledPostsPanel,
  PostAndEmailDialog
} from './index';

// Import shared utilities
import { API_URL } from '../utils/constants';
import UploadProgress from '@/components/shared/UploadProgress';
import { uploadWithProgress, uploadVideoWithProgress } from '@/lib/uploadVideoWithProgress';
import { IMAGE_ACCEPT, isImageFile } from '@/lib/imageFiles';

// R39 (2026-09-07): post drafts. A member lost a long weekly memo by clicking a
// link inside the post they were writing (the desktop shell reloads the app
// on internal new-tab links); drafts are also lost on tab switches and
// reloads. Saved per user + church in localStorage, 500 ms debounced, flushed
// synchronously on pagehide/unmount, cleared on post/schedule/email/discard
// and on sign-out. Everything in the composer is already a URL or string.
const POST_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Plain text of an HTML fragment (entities decoded, tags removed).
const decodeHtmlText = (html) => {
  try { return new DOMParser().parseFromString(html || '', 'text/html').body.textContent || ''; }
  catch { return html || ''; }
};

export const POST_DRAFT_KEY_PREFIX = 'post_draft_';
const postDraftKey = (churchId, userId) => `${POST_DRAFT_KEY_PREFIX}${churchId || 'none'}_${userId || 'anon'}`;
function readPostDraft(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !d.savedAt || Date.now() - d.savedAt > POST_DRAFT_TTL_MS) { localStorage.removeItem(key); return null; }
    return d;
  } catch { return null; }
}
function writePostDraft(key, snapshot) {
  try {
    if (snapshot) localStorage.setItem(key, JSON.stringify({ ...snapshot, savedAt: Date.now() }));
    else localStorage.removeItem(key);
  } catch { /* storage full or blocked: drafts are best-effort */ }
}

function CmpRow({ isStudio, children }) {
  return isStudio ? <div className="st-cmp-top">{children}</div> : <>{children}</>;
}

function stripHtmlToText(html) {
  return String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
}

function FeedView({ user, posts, setPosts, onRefresh, feedError = false, highlightedPostId, highlightedCommentId, isOnline = true, unreadPostIds, onPostSeen, leadersOnlyPosting = false }) {
  const { isStudio } = useDesign();
  const [newPostContent, setNewPostContent] = useState('');
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [voiceMemo, setVoiceMemo] = useState(null); // { data: url, duration: seconds, mimeType }
  const [posting, setPosting] = useState(false);
  const highlightRef = useRef(null);
  
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]); // Array of { url, filename }
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(null);
  const imageInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of { url, filename, size, type }
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  // Video upload state
  const [selectedVideo, setSelectedVideo] = useState(null); // { url, thumbnail_url, duration, width, height }
  const [videoUploadProgress, setVideoUploadProgress] = useState(null); // null = not uploading, 0-100 = percent
  const videoInputRef = useRef(null);
  
  // Poll state
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollAllowOther, setPollAllowOther] = useState(false);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  
  // Infinite scroll state
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);

  // Scheduled posts state (admin only)
  const [showPostMenu, setShowPostMenu] = useState(false);
  // 104: Studio drives bold / italic / list from the composer's own tool row.
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showScheduledPosts, setShowScheduledPosts] = useState(false);
  const [scheduledPostsCount, setScheduledPostsCount] = useState(0);
  const [showMobilePostMenu, setShowMobilePostMenu] = useState(false);
  
  // Post & Email state
  const [showPostAndEmailDialog, setShowPostAndEmailDialog] = useState(false);
  const [postAndEmailLoading, setPostAndEmailLoading] = useState(false);
  
  // Group mention state
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [groupPickerOpensDown, setGroupPickerOpensDown] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const groupPickerRef = React.useRef(null);

  // @mention state for posts
  const [postMentions, setPostMentions] = useState([]);
  // Initialize from session-scoped cache so subsequent navigations open instantly.
  // Note: groups are intentionally NOT wired into the post composer — @-mentioning
  // a group only notifies that group, but doesn't restrict post visibility, which
  // is confusing UX. Group mentions live in COMMENTS only.
  const [postMembers, setPostMembers] = useState(() => peekMembers(user?.church_id));
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  // Fetch group suggestions for autocomplete (debounced ~250ms to avoid a
  // request per keystroke; `cancelled` guards against a stale slow response
  // overwriting a newer one).
  useEffect(() => {
    if (!showGroupPicker || !user?.church_id) return;
    let cancelled = false;
    const q = groupSearchQuery.replace(/^@/, '');
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/groups/autocomplete?church_id=${user.church_id}&q=${encodeURIComponent(q)}${user?.id ? `&user_id=${encodeURIComponent(user.id)}` : ''}`)
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          if (!cancelled) setGroupSuggestions(data.filter(g => !selectedGroups.some(s => s.id === g.id)));
        })
        .catch(() => { if (!cancelled) setGroupSuggestions([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showGroupPicker, groupSearchQuery, user?.church_id, selectedGroups]);

  // Close group picker on outside click
  useEffect(() => {
    if (!showGroupPicker) return;
    const handleClick = (e) => {
      if (groupPickerRef.current && !groupPickerRef.current.contains(e.target)) {
        setShowGroupPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showGroupPicker]);

  const handleSelectGroup = (group) => {
    setSelectedGroups(prev => [...prev, group]);
    setShowGroupPicker(false);
    setGroupSearchQuery('');
  };

  // Lazy-fetch church members for @mentions — uses shared module cache.
  const fetchPostMembers = React.useCallback(async () => {
    if (postMembers !== null || !user?.church_id) return;
    const data = await getMentionMembers(user.church_id);
    // Cache returns null on transient failure; only commit to state on
    // a real result. Leaving state at null lets the next render retry.
    if (data !== null) setPostMembers(data);
  }, [user?.church_id, postMembers]);

  const handleRemoveGroup = (groupId) => {
    setSelectedGroups(prev => prev.filter(g => g.id !== groupId));
  };

  // Fetch scheduled posts count for admin badge
  useEffect(() => {
    if (isAdmin && user?.church_id) {
      fetch(`${API_URL}/api/comms/v3/scheduled-posts?user_id=${user.id}&church_id=${user.church_id}`)
        .then(res => res.ok ? res.json() : { scheduled_posts: [] })
        .then(data => setScheduledPostsCount(data.scheduled_posts?.length || 0))
        .catch(() => setScheduledPostsCount(0));
    }
  }, [isAdmin, user?.id, user?.church_id]);

  // Listen for background email results via WebSocket
  useEffect(() => {
    const handleEmailResults = (event) => {
      const data = event.detail;
      if (data.emails_failed > 0) {
        toast.error(
          `${data.emails_failed} email${data.emails_failed > 1 ? 's' : ''} failed to send: ${data.failed_addresses.join(', ')}`,
          { duration: 15000 }
        );
      } else if (data.emails_sent > 0) {
        toast.success(`All ${data.emails_sent} emails sent successfully!`);
      }
    };
    window.addEventListener('ws_email_results', handleEmailResults);
    return () => window.removeEventListener('ws_email_results', handleEmailResults);
  }, []);

  // Check if content is valid for posting
  // Empty paragraphs (<p></p>, <p><br></p>, <p>&nbsp;</p>) are not content: no draft, no post.
  const hasText = !!newPostContent.replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '').trim();
  const hasValidContent = hasText || selectedGif || youtubeUrl || voiceMemo || selectedVideo || selectedImages.length > 0 || selectedFiles.length > 0 || (pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2);

  // A mention only counts while its @text is still in the post (the editor
  // never prunes its own list; a restored draft's list must not outlive edits).
  useEffect(() => {
    setPostMentions((prev) => {
      if (!prev.length) return prev;
      // Compare against the decoded text: the editor stores "&" as "&amp;", so
      // "@Tom & Jerry" must not be pruned on the next keystroke.
      const plain = decodeHtmlText(newPostContent);
      const next = prev.filter((m) => plain.includes(`@${m.tag || m.name}`));
      return next.length === prev.length ? prev : next;
    });
  }, [newPostContent]);

  // ── Draft autosave (R39) ──
  const draftKey = postDraftKey(user?.church_id, user?.id);
  const [draftRestored, setDraftRestored] = useState(false);
  const draftTimerRef = useRef(null);
  const draftLoadedRef = useRef(false);
  const draftSnapshotRef = useRef(null);

  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    const d = readPostDraft(draftKey);
    if (!d) return;
    if (d.content) setNewPostContent(d.content);
    if (d.gif) setSelectedGif(d.gif);
    if (d.youtubeUrl) setYoutubeUrl(d.youtubeUrl);
    if (d.voiceMemo) setVoiceMemo(d.voiceMemo);
    if (Array.isArray(d.images) && d.images.length) setSelectedImages(d.images);
    if (Array.isArray(d.files) && d.files.length) setSelectedFiles(d.files);
    if (d.video) setSelectedVideo(d.video);
    if (d.poll) {
      setShowPollCreator(true);
      setPollQuestion(d.poll.question || '');
      setPollOptions(Array.isArray(d.poll.options) && d.poll.options.length >= 2 ? d.poll.options : ['', '']);
      setPollAllowOther(!!d.poll.allowOther);
      setPollAllowMultiple(!!d.poll.allowMultiple);
    }
    if (Array.isArray(d.groups) && d.groups.length) setSelectedGroups(d.groups);
    if (Array.isArray(d.mentions) && d.mentions.length) setPostMentions(d.mentions);
    setDraftRestored(true);
  }, [draftKey]);

  useEffect(() => {
    if (!draftLoadedRef.current) return undefined;
    const snapshot = hasValidContent ? {
      content: newPostContent,
      gif: selectedGif,
      youtubeUrl,
      voiceMemo,
      images: selectedImages,
      files: selectedFiles,
      video: selectedVideo,
      poll: (showPollCreator && (pollQuestion.trim() || pollOptions.some(o => o.trim())))
        ? { question: pollQuestion, options: pollOptions, allowOther: pollAllowOther, allowMultiple: pollAllowMultiple }
        : null,
      groups: selectedGroups,
      mentions: postMentions,
    } : null;
    draftSnapshotRef.current = snapshot;
    if (!snapshot && draftRestored) setDraftRestored(false);
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => writePostDraft(draftKey, snapshot), 500);
    return () => clearTimeout(draftTimerRef.current);
  }, [draftKey, draftRestored, hasValidContent, newPostContent, selectedGif, youtubeUrl, voiceMemo, selectedImages, selectedFiles, selectedVideo, showPollCreator, pollQuestion, pollOptions, pollAllowOther, pollAllowMultiple, selectedGroups, postMentions]);

  useEffect(() => {
    // Flush synchronously when the page is going away or the view unmounts
    // (tab switch inside Team Comms) so a reload never races the debounce.
    const flush = () => {
      if (!draftLoadedRef.current) return;
      clearTimeout(draftTimerRef.current);
      writePostDraft(draftKey, draftSnapshotRef.current);
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    return () => {
      flush();
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
    };
  }, [draftKey]);

  const discardDraft = () => {
    setNewPostContent('');
    setSelectedGif(null);
    setYoutubeUrl('');
    setVoiceMemo(null);
    setSelectedImages([]);
    setSelectedFiles([]);
    setSelectedVideo(null);
    setShowPollCreator(false);
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollAllowOther(false);
    setPollAllowMultiple(false);
    setSelectedGroups([]);
    setPostMentions([]);
    setDraftRestored(false);
    draftSnapshotRef.current = null;
    writePostDraft(draftKey, null);
  };

  // Scroll to highlighted post or show toast if not found
  useEffect(() => {
    if (highlightedPostId && posts.length > 0) {
      // Check if the highlighted post exists in the loaded posts
      const postExists = posts.some(p => p.id === highlightedPostId);
      
      if (postExists && highlightRef.current) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else if (!postExists) {
        // Post not found - might be deleted
        // Wait a bit to allow posts to load first
        const timer = setTimeout(() => {
          const stillNotFound = !posts.some(p => p.id === highlightedPostId);
          if (stillNotFound) {
            toast.error('This post has been deleted', {
              description: 'The content you were looking for is no longer available.'
            });
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [highlightedPostId, posts]);

  // Infinite scroll - load more posts when scrolling near bottom
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && posts.length >= 20) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, posts.length]);

  // Smart Read Detection: mark notifications as read when user scrolls past a post
  const seenPostsRef = useRef(new Set());
  const postObserverRef = useRef(null);

  useEffect(() => {
    if (!onPostSeen || !unreadPostIds || unreadPostIds.size === 0) return;

    // Disconnect previous observer
    if (postObserverRef.current) {
      postObserverRef.current.disconnect();
    }

    const timers = new Map();

    postObserverRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const postId = entry.target.dataset.postId;
          if (!postId) continue;

          if (entry.isIntersecting) {
            // Post is visible — start a 1s timer before marking as read
            if (!seenPostsRef.current.has(postId) && unreadPostIds.has(postId)) {
              const timer = setTimeout(() => {
                seenPostsRef.current.add(postId);
                onPostSeen(postId);
              }, 1000);
              timers.set(postId, timer);
            }
          } else {
            // Post scrolled out — cancel the timer if it hasn't fired yet
            if (timers.has(postId)) {
              clearTimeout(timers.get(postId));
              timers.delete(postId);
            }
          }
        }
      },
      { threshold: 0.3 } // 30% of the post must be visible
    );

    // Observe all post elements with data-post-id
    const postElements = document.querySelectorAll('[data-post-id]');
    postElements.forEach(el => {
      const postId = el.dataset.postId;
      if (unreadPostIds.has(postId) && !seenPostsRef.current.has(postId)) {
        postObserverRef.current.observe(el);
      }
    });

    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
      if (postObserverRef.current) {
        postObserverRef.current.disconnect();
      }
    };
  }, [onPostSeen, unreadPostIds, posts.length]);

  // Post View Tracking: record views for the "Read by" feature
  const viewedPostsRef = useRef(new Set());
  const viewObserverRef = useRef(null);

  // Seed viewedPostsRef from server data so we skip redundant view recording calls
  useEffect(() => {
    posts.forEach(p => {
      if (p.viewed_by_me && p.id) viewedPostsRef.current.add(p.id);
    });
  }, [posts]);

  useEffect(() => {
    if (!user?.id) return;

    if (viewObserverRef.current) {
      viewObserverRef.current.disconnect();
    }

    const timers = new Map();

    viewObserverRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const postId = entry.target.dataset.postId;
          if (!postId || postId.startsWith('temp-')) continue;

          if (entry.isIntersecting && !viewedPostsRef.current.has(postId)) {
            const timer = setTimeout(() => {
              viewedPostsRef.current.add(postId);
              // Fire-and-forget view recording
              fetch(`${API_URL}/api/comms/v3/posts/${postId}/view?user_id=${encodeURIComponent(user.id)}`, { method: 'POST' }).catch(() => {});
              // Only bump view_count if this is a genuinely new view (not already viewed by me)
              setPosts(prev => prev.map(p => {
                if (p.id === postId && !p.viewed_by_me) {
                  return { ...p, view_count: (p.view_count || 0) + 1, viewed_by_me: true };
                }
                return p;
              }));
            }, 1500);
            timers.set(postId, timer);
          } else if (!entry.isIntersecting && timers.has(postId)) {
            clearTimeout(timers.get(postId));
            timers.delete(postId);
          }
        }
      },
      { threshold: 0.3 }
    );

    const postElements = document.querySelectorAll('[data-post-id]');
    postElements.forEach(el => {
      const postId = el.dataset.postId;
      if (postId && !viewedPostsRef.current.has(postId)) {
        viewObserverRef.current.observe(el);
      }
    });

    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
      if (viewObserverRef.current) viewObserverRef.current.disconnect();
    };
  }, [user?.id, posts.length, setPosts]);

  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    
    try {
      const churchId = user?.church_id || '';
      const offset = posts.length;
      const response = await fetch(`${API_URL}/api/comms/v3/posts?church_id=${encodeURIComponent(churchId)}&limit=20&offset=${offset}&viewer_id=${encodeURIComponent(user.id)}`);
      if (response.ok) {
        const data = await response.json();
        const newPosts = data.posts || [];
        if (newPosts.length > 0) {
          setPosts(prev => [...prev, ...newPosts]);
        }
        setHasMore(data.has_more !== false && newPosts.length === 20);
      }
    } catch (err) {
      console.error('Error loading more posts:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Regex patterns for video URLs
  const videoUrlPatterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)/
  ];

  // Extract video URL from text
  const extractVideoUrl = (text) => {
    for (const pattern of videoUrlPatterns) {
      const match = text.match(pattern);
      if (match) {
        // Return the full matched URL
        const fullMatch = match[0];
        return fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`;
      }
    }
    return null;
  };

  // Build embed object from video URL
  const buildEmbedObject = (url) => {
    if (!url) return null;
    
    // YouTube patterns
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          type: 'youtube',
          video_id: match[1],
          url: url
        };
      }
    }
    
    // Loom pattern
    const loomPattern = /loom\.com\/share\/([a-zA-Z0-9]+)/;
    const loomMatch = url.match(loomPattern);
    if (loomMatch) {
      return {
        type: 'loom',
        video_id: loomMatch[1],
        url: url
      };
    }
    
    return null;
  };

  // Handle text change and auto-detect video URLs
  const handleTextChange = (e) => {
    const text = e.target.value;
    setNewPostContent(text);
    
    // Auto-detect video URL if none is set
    if (!youtubeUrl) {
      const detectedUrl = extractVideoUrl(text);
      if (detectedUrl) {
        setYoutubeUrl(detectedUrl);
      }
    }
  };

  // Handle image selection and upload
  const uploadImageFiles = async (files) => {
    if (files.length === 0) return;
    
    // Check total image count
    if (selectedImages.length + files.length > 10) {
      toast.error('Maximum 10 images allowed per post.');
      return;
    }

    // Validate file sizes (max 5MB each)
    const oversized = files.filter(f => f.size > 7 * 1024 * 1024); // R15: server cap is 7 MB
    if (oversized.length > 0) {
      toast.error('Image file size must be under 7MB.');
      return;
    }
    
    setUploadingImages(true);
    setImageUploadProgress(0);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const data = await uploadWithProgress(
        `${API_URL}/api/comms/v3/upload/images?user_id=${encodeURIComponent(user.id)}`,
        formData,
        (pct) => setImageUploadProgress(pct),
        0 // no server processing time for images
      );
      setSelectedImages(prev => [...prev, ...data.images]);
    } catch (err) {
      console.error('Error uploading images:', err);
      const said = err?.message && !/^(Upload failed|Network error)/.test(err.message) ? err.message : null;
      toast.error(said || 'Image upload failed. Please check the file size (max 7MB) and try again.');
    } finally {
      setUploadingImages(false);
      setImageUploadProgress(null);
      // Reset file input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleImageSelect = (e) => {
    uploadImageFiles(Array.from(e.target.files || []));
  };

  const uploadAttachmentFiles = async (files) => {
    if (files.length === 0) return;

    if (selectedFiles.length + files.length > 5) {
      toast.error('Maximum 5 files allowed per post.');
      return;
    }

    const oversized = files.filter(f => f.size > 60 * 1024 * 1024); // R15: server cap is 60 MB
    if (oversized.length > 0) {
      toast.error('File size must be under 60MB.');
      return;
    }

    setUploadingFiles(true);
    setFileUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const data = await uploadWithProgress(
        `${API_URL}/api/comms/v3/upload/files?user_id=${encodeURIComponent(user.id)}`,
        formData,
        (pct) => setFileUploadProgress(pct),
        0
      );
      setSelectedFiles(prev => [...prev, ...data.files]);
    } catch (err) {
      console.error('Error uploading files:', err);
      toast.error(err.message || 'File upload failed.');
    } finally {
      setUploadingFiles(false);
      setFileUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    uploadAttachmentFiles(Array.from(e.target.files || []));
  };

  // Handle video selection and upload
  const processVideoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file.');
      return;
    }

    if (file.size > 400 * 1024 * 1024) {
      toast.error('Video must be under 400MB.');
      return;
    }

    // Client-side duration check
    try {
      const duration = await new Promise((resolve, reject) => {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => { URL.revokeObjectURL(vid.src); resolve(vid.duration); };
        vid.onerror = () => { URL.revokeObjectURL(vid.src); reject(new Error('Cannot read video')); };
        vid.src = URL.createObjectURL(file);
      });
      if (duration > 240) {
        toast.error('Video must be under 4 minutes. This video is ' + Math.floor(duration / 60) + ':' + String(Math.floor(duration % 60)).padStart(2, '0') + '.');
        return;
      }
    } catch {
      // If we can't read duration client-side, server will enforce
    }

    setVideoUploadProgress(0);
    try {
      const data = await uploadVideoWithProgress(
        `${API_URL}/api/comms/v3/upload/video?user_id=${encodeURIComponent(user.id)}&prefix=posts`,
        file,
        (pct) => setVideoUploadProgress(pct)
      );
      setSelectedVideo(data.video);
      toast.success('Video uploaded!');
    } catch (err) {
      console.error('Error uploading video:', err);
      toast.error(err.message || 'Video upload failed.');
    } finally {
      setVideoUploadProgress(null);
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (videoInputRef.current) videoInputRef.current.value = '';
    processVideoFile(file);
  };

  // ---- DRAG & DROP + PASTE ----
  // Route any mix of files (dropped or pasted onto the composer) into the
  // same attach flows as the toolbar buttons.
  const POST_DOC_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'mp3', 'm4a', 'wav', 'ogg', 'aac', 'flac'];
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const dragHasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const routeIncomingFiles = (files) => {
    if (posting || files.length === 0) return;

    const images = files.filter(isImageFile);
    const videos = files.filter(f => f.type.startsWith('video/'));
    const docs = [];
    const unsupported = [];
    files.forEach(f => {
      if (isImageFile(f) || f.type.startsWith('video/')) return;
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (POST_DOC_EXTENSIONS.includes(ext)) docs.push(f);
      else unsupported.push(f);
    });

    if (images.length > 0) uploadImageFiles(images);
    if (videos.length > 0) {
      if (videos.length > 1) toast.error('Only one video per post — attaching the first one.');
      if (selectedVideo || videoUploadProgress !== null) toast.error('A video is already attached.');
      else processVideoFile(videos[0]);
    }
    if (docs.length > 0) uploadAttachmentFiles(docs);
    if (unsupported.length > 0) {
      toast.error(`Unsupported file type: ${unsupported.map(f => f.name).join(', ')}`);
    }
  };

  const handleDragEnter = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!dragHasFiles(e)) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    e.stopPropagation(); // keep the rich-text editor from handling the drop itself
    dragCounterRef.current = 0;
    setIsDragOver(false);
    routeIncomingFiles(Array.from(e.dataTransfer.files || []));
  };

  const handlePasteCapture = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length === 0) return; // plain text — let the editor handle it
    e.preventDefault();
    e.stopPropagation();
    routeIncomingFiles(files);
  };

  const handleCreatePost = async () => {
    // Check if we have content OR a poll
    const hasPoll = pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2;
    if (!newPostContent.trim() && !selectedGif && !youtubeUrl && !voiceMemo && selectedImages.length === 0 && !hasPoll && !selectedVideo) return;
    
    setPosting(true);
    
    // Store current values for optimistic update
    const userName = user?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'You';
    
    // Remove the video URL from content if it was auto-detected
    let cleanContent = newPostContent;
    if (youtubeUrl) {
      for (const pattern of videoUrlPatterns) {
        cleanContent = cleanContent.replace(pattern, '').trim();
      }
      cleanContent = cleanContent.replace(new RegExp(youtubeUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '').trim();
    }
    
    // Build poll data if present
    const pollData = hasPoll ? {
      question: pollQuestion.trim(),
      options: pollOptions.filter(o => o.trim()).map(o => ({ text: o.trim(), votes: [] })),
      allow_other: pollAllowOther,
      allow_multiple: pollAllowMultiple,
      other_responses: []
    } : null;
    
    // Create optimistic post for immediate UI update
    const optimisticId = `temp-${Date.now()}`;
    const optimisticPost = {
      id: optimisticId,
      user_id: user.id,
      church_id: user.church_id,
      content: cleanContent,
      gif_url: selectedGif?.url || null,
      media_urls: selectedImages.map(img => img.url),
      voice_memo_data: voiceMemo?.data || null,
      voice_memo_mime: voiceMemo?.mimeType || null,
      voice_memo_duration: voiceMemo?.duration || null,
      poll: pollData,
      embed: buildEmbedObject(youtubeUrl),
      is_pinned: false,
      author: {
        id: user.id,
        name: userName,
        avatar_url: user.avatar || null
      },
      reactions: {},
      comment_count: 0,
      groups: selectedGroups.length > 0 ? selectedGroups.map(g => ({ id: g.id, name: g.name, tag: g.tag, color: g.color })) : undefined,
      attachments: selectedFiles,
      mentions: postMentions,
      video: selectedVideo || undefined,
      _isOptimistic: true
    };
    
    // Immediately add optimistic post to UI. Insert it at the top of the
    // UNPINNED section (not the absolute top) so a brand-new post never appears
    // above pinned posts — the backend always returns pinned posts first, and
    // the feed renders in raw array order. (Mobile doesn't optimistic-insert, so
    // it was never affected.)
    setPosts(prev => {
      const firstUnpinned = prev.findIndex(p => !p.is_pinned);
      if (firstUnpinned === -1) return [...prev, optimisticPost];
      return [...prev.slice(0, firstUnpinned), optimisticPost, ...prev.slice(firstUnpinned)];
    });
    
    // Clear form immediately for better UX
    const savedContent = newPostContent;
    const savedGif = selectedGif;
    const savedYoutubeUrl = youtubeUrl;
    const savedEmbed = buildEmbedObject(youtubeUrl);
    const savedVoiceMemo = voiceMemo;
    const savedImages = selectedImages;
    const savedPollQuestion = pollQuestion;
    const savedPollOptions = pollOptions;
    const savedPollAllowOther = pollAllowOther;
    const savedPollAllowMultiple = pollAllowMultiple;
    const savedFiles = selectedFiles;
    const savedVideo = selectedVideo;
    
    setNewPostContent('');
    setSelectedGif(null);
    setYoutubeUrl('');
    setVoiceMemo(null);
    setSelectedImages([]);
    setSelectedFiles([]);
    setSelectedVideo(null);
    setShowPollCreator(false);
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollAllowOther(false);
    setPollAllowMultiple(false);
    setPostMentions([]);
    const savedGroups = [...selectedGroups];
    const savedGroupIds = savedGroups.map(g => g.id);
    setSelectedGroups([]);
    
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          church_id: user.church_id,
          content: cleanContent,
          gif_url: savedGif?.url || null,
          media_urls: savedImages.map(img => img.url),
          voice_memo_data: savedVoiceMemo?.data || null,
          voice_memo_mime: savedVoiceMemo?.mimeType || null,
          voice_memo_duration: savedVoiceMemo?.duration || null,
          poll: pollData,
          embed: savedEmbed,
          group_ids: savedGroupIds,
          attachments: savedFiles,
          mentions: postMentions,
          video: savedVideo || undefined
        })
      });

      if (response.ok) {
        const realPost = await response.json();
        // Replace optimistic post with real post, keeping voice memo data for continuity
        setPosts(prev => prev.map(p => p.id === optimisticId ? { 
          ...realPost, 
          voice_memo_data: savedVoiceMemo?.data || realPost.voice_memo_data,
          voice_memo_duration: savedVoiceMemo?.duration || realPost.voice_memo_duration,
          embed: savedEmbed || realPost.embed,
          _isOptimistic: false 
        } : p));
      } else {
        // On error, remove optimistic post and restore form
        setPosts(prev => prev.filter(p => p.id !== optimisticId));
        setNewPostContent(savedContent);
        setSelectedGif(savedGif);
        setYoutubeUrl(savedYoutubeUrl);
        setVoiceMemo(savedVoiceMemo);
        setSelectedImages(savedImages);
        setSelectedGroups(savedGroups);
        setSelectedVideo(savedVideo);
        if (hasPoll) {
          setShowPollCreator(true);
          setPollQuestion(savedPollQuestion);
          setPollOptions(savedPollOptions);
          setPollAllowOther(savedPollAllowOther);
        setPollAllowMultiple(savedPollAllowMultiple);
        }
        console.error('Error creating post: server returned', response.status);
        // Surface the server's reason (e.g. "Only leaders can post in this church")
        const errBody = await response.json().catch(() => ({}));
        toast.error(typeof errBody.detail === 'string' ? errBody.detail : "Post couldn't be published. Please try again.");
      }
    } catch (err) {
      // On error, remove optimistic post and restore form
      setPosts(prev => prev.filter(p => p.id !== optimisticId));
      setNewPostContent(savedContent);
      setSelectedGif(savedGif);
      setYoutubeUrl(savedYoutubeUrl);
      setVoiceMemo(savedVoiceMemo);
      setSelectedImages(savedImages);
      setSelectedVideo(savedVideo);
      setSelectedGroups(savedGroups);
      if (hasPoll) {
        setShowPollCreator(true);
        setPollQuestion(savedPollQuestion);
        setPollOptions(savedPollOptions);
        setPollAllowOther(savedPollAllowOther);
        setPollAllowMultiple(savedPollAllowMultiple);
      }
      console.error('Error creating post:', err);
    } finally {
      setPosting(false);
    }
  };

  // Handle scheduling a post (admin only)
  const handleSchedulePost = async (scheduledForISO) => {
    // Check if we have content OR a poll
    const hasPoll = pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2;
    if (!newPostContent.trim() && !selectedGif && !youtubeUrl && !voiceMemo && selectedImages.length === 0 && !hasPoll) {
      toast.error('Please add some content to schedule');
      return;
    }

    // Remove the video URL from content if it was auto-detected
    let cleanContent = newPostContent;
    if (youtubeUrl) {
      for (const pattern of videoUrlPatterns) {
        cleanContent = cleanContent.replace(pattern, '').trim();
      }
      cleanContent = cleanContent.replace(new RegExp(youtubeUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '').trim();
    }

    // Build poll data if present
    const pollData = hasPoll ? {
      question: pollQuestion.trim(),
      options: pollOptions.filter(o => o.trim()).map(o => ({ text: o.trim(), votes: [] })),
      allow_other: pollAllowOther,
      allow_multiple: pollAllowMultiple,
      other_responses: []
    } : null;

    try {
      const response = await fetch(`${API_URL}/api/comms/v3/scheduled-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          church_id: user.church_id,
          content: cleanContent,
          media_urls: selectedImages.map(img => img.url),
          gif_url: selectedGif?.url || null,
          poll: pollData,
          voice_memo_data: voiceMemo?.data || null,
          voice_memo_mime: voiceMemo?.mimeType || null,
          voice_memo_duration: voiceMemo?.duration || null,
          embed: buildEmbedObject(youtubeUrl),
          scheduled_for: scheduledForISO,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          group_ids: selectedGroups.map(g => g.id),
          attachments: selectedFiles,
          mentions: postMentions
        })
      });

      if (response.ok) {
        toast.success('Post scheduled!', {
          description: `Will be published at the selected time`
        });
        
        // Clear form
        setNewPostContent('');
        setSelectedGif(null);
        setYoutubeUrl('');
        setVoiceMemo(null);
        setSelectedImages([]);
        setSelectedFiles([]);
        setSelectedVideo(null); // scheduled posts carry no video yet; do not leave it as a draft
        setShowPollCreator(false);
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollAllowOther(false);
        setSelectedGroups([]);
        setPostMentions([]);
        
        // Update scheduled posts count
        setScheduledPostsCount(prev => prev + 1);
      } else {
        // Handle error response
        let errorMessage = 'Failed to schedule post';
        if (response.status === 403) {
          errorMessage = 'Only admins can schedule posts';
        } else {
          try {
            const error = await response.json();
            errorMessage = error.detail || errorMessage;
          } catch {
            // Response body may have been consumed by middleware
            errorMessage = `Error: ${response.status} ${response.statusText}`;
          }
        }
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error scheduling post:', err);
      toast.error('Failed to schedule post');
    }
  };

  // Handle Post and Email (admin only)
  const handlePostAndEmail = async () => {
    if (!hasValidContent) {
      toast.error('Please add some content to post');
      return;
    }

    // Remove the video URL from content if it was auto-detected
    let cleanContent = newPostContent;
    if (youtubeUrl) {
      for (const pattern of videoUrlPatterns) {
        cleanContent = cleanContent.replace(pattern, '').trim();
      }
      cleanContent = cleanContent.replace(new RegExp(youtubeUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '').trim();
    }

    // Build poll data if present
    const pollData = (pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2) ? {
      question: pollQuestion.trim(),
      options: pollOptions.filter(o => o.trim()).map(o => ({ text: o.trim(), votes: [] })),
      allow_other: pollAllowOther,
      allow_multiple: pollAllowMultiple,
      other_responses: []
    } : null;

    setPostAndEmailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/post-and-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          church_id: user.church_id,
          content: cleanContent,
          media_urls: selectedImages.map(img => img.url),
          gif_url: selectedGif?.url || null,
          poll: pollData,
          voice_memo_data: voiceMemo?.data || null,
          voice_memo_mime: voiceMemo?.mimeType || null,
          voice_memo_duration: voiceMemo?.duration || null,
          embed: buildEmbedObject(youtubeUrl),
          group_ids: selectedGroups.map(g => g.id),
          attachments: selectedFiles,
          mentions: postMentions
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Posted & emailing ${data.emails_queued} team members!`);
        
        // Clear form (files/video too, or the autosave keeps them as a draft)
        setNewPostContent('');
        setSelectedGif(null);
        setYoutubeUrl('');
        setVoiceMemo(null);
        setSelectedImages([]);
        setSelectedFiles([]);
        setSelectedVideo(null);
        setShowPollCreator(false);
        setSelectedGroups([]);
        setPostMentions([]);
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollAllowOther(false);
        setPollAllowMultiple(false);
        setShowPostAndEmailDialog(false);
        
        // Refresh feed
        if (onRefresh) onRefresh();
      } else {
        let errorMsg = `Server error (${response.status})`;
        try {
          const error = await response.json();
          errorMsg = typeof error.detail === 'string' ? error.detail : (error.detail ? JSON.stringify(error.detail) : errorMsg);
        } catch { /* response wasn't JSON */ }
        console.error('Post and email failed:', response.status, errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error posting and emailing:', err);
      toast.error('Network error — please check your connection and try again');
    } finally {
      setPostAndEmailLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleCreatePost();
    }
  };

  // Get display name from user object
  const getUserDisplayName = () => {
    return user?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'You';
  };

  // Format duration for voice memo
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div data-testid="feed-view" className={isStudio ? 'st-feed' : 'max-w-2xl mx-auto py-6 px-1 md:px-4'}>
      {/* R3: members see why the composer is missing */}
      {leadersOnlyPosting && !isAdmin && (
        <div className={isStudio ? 'st-fdnotice' : "mb-6 rounded-xl border border-slate-200 dark:border-white/10 bg-white/70 dark:bg-[#2d2f33]/70 px-4 py-3 text-sm text-slate-500 dark:text-slate-400"} data-testid="leaders-only-notice">
          Only leaders can post here. You can still comment, react and message your team.
        </div>
      )}
      {/* Post Composer - Disabled when offline */}
      <Card
        data-testid="post-composer"
        className={`relative ${isStudio ? 'st-composer' : 'mb-6 shadow-lg border-0 bg-white/90 dark:bg-[#2d2f33]/90 backdrop-blur-sm'} ${!isOnline ? 'opacity-60' : ''} ${showGroupPicker ? 'z-10' : ''} ${leadersOnlyPosting && !isAdmin ? 'hidden' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOverCapture={(e) => { if (dragHasFiles(e)) e.preventDefault(); }}
        onDropCapture={handleDrop}
        onPasteCapture={handlePasteCapture}
      >
        {/* Drag & drop overlay */}
        {isDragOver && (
          <div
            className={`absolute inset-0 z-50 flex items-center justify-center pointer-events-none ${isStudio ? 'st-fddrop' : 'rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/90 dark:bg-blue-950/80'}`}
            data-testid="post-drop-overlay"
          >
            <span className={isStudio ? undefined : "text-sm font-medium text-blue-600 dark:text-blue-300"}>Drop files to attach</span>
          </div>
        )}
        <CardContent className={isStudio ? "st-cmp-body" : "pt-4"}>
          {!isOnline && (
            <div className={isStudio ? 'st-fdoffline' : "mb-3 text-center py-2 px-3 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-sm rounded-lg"}>
              You&apos;re offline — posting is disabled
            </div>
          )}
          {draftRestored && (
            <div className={isStudio ? 'st-fddraft' : "mb-2 flex items-center justify-between rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-300"} data-testid="post-draft-restored">
              <span>Draft restored</span>
              <button type="button" className="underline hover:no-underline" onClick={discardDraft} data-testid="post-draft-discard">Discard</button>
            </div>
          )}
          <div className={!isOnline ? 'pointer-events-none' : ''}>
            {/* Studio wraps the avatar and the editor in one row; classic keeps the
                editor as a direct child so its DOM is untouched. */}
            <CmpRow isStudio={isStudio}>
            {isStudio && !stripHtmlToText(newPostContent).trim() && (
              /* The editor has no placeholder extension, so Studio draws the
                 mockup's prompt over the empty document. */
              <span className="st-cmp-ph" aria-hidden="true">Share something with the team…</span>
            )}
            <RichTextEditor
              content={newPostContent}
              onChange={(html) => {
                setNewPostContent(html);
                // Check for YouTube/Loom URLs in plain text
                const textContent = html.replace(/<[^>]*>/g, ' ');
                const urlMatch = textContent.match(/(https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/|youtube\.com\/shorts\/|loom\.com\/share\/)[^\s<]+)/i);
                if (urlMatch) {
                  setYoutubeUrl(urlMatch[1]);
                }
              }}
              onSubmit={handleCreatePost}
              placeholder={isStudio ? 'Share something with the team…' : 'Share something with your team... (@ to mention, paste YouTube or Loom links to auto-embed)'}
              minHeight="80px"
              members={postMembers}
              currentUserId={user?.id}
              onMentionsChange={(list) => setPostMentions((prev) => {
                // Merge: the editor tracks only mentions added since mount, so a
                // restored draft's mentions (R39) must not be dropped.
                const key = (m) => m.group_id ? `g:${m.group_id}` : `u:${m.user_id}`;
                const seen = new Set((list || []).map(key));
                return [...(list || []), ...prev.filter((m) => !seen.has(key(m)))];
              })}
              onMentionTrigger={() => {
                // Lazy-load member data on first @ — cache prevents redundant calls.
                // Groups are intentionally NOT loaded for posts (see comment on postMembers state).
                if (postMembers === null) fetchPostMembers();
              }}
            />
            </CmpRow>
            
            {/* Preview selected GIF */}
            {selectedGif && (
              <div className="relative mt-2 inline-block">
                <img src={selectedGif.url} alt="Selected GIF" className="rounded-lg max-h-32" />
                <button
                  onClick={() => setSelectedGif(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-slate-800 rounded-full flex items-center justify-center text-white"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* YouTube/Loom URL preview */}
            {youtubeUrl && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-slate-100 dark:bg-[#3a3d41] rounded-lg">
                {youtubeUrl.includes('loom.com') ? (
                  <div className="w-8 h-8 bg-purple-600 rounded flex items-center justify-center">
                    <Play className="w-4 h-4 text-white" />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center">
                    <Video className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {youtubeUrl.includes('loom.com') ? 'Loom Video' : 'YouTube Video'}
                  </span>
                  <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{youtubeUrl}</p>
                </div>
                <button onClick={() => setYoutubeUrl('')} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Voice Memo Preview */}
            {voiceMemo && (
              <div className="flex items-center gap-3 mt-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/15 dark:to-indigo-500/15 rounded-lg border border-blue-100 dark:border-blue-500/30">
                <button 
                  onClick={() => {
                    const isUrl = voiceMemo.data && voiceMemo.data.startsWith('http');
                    const src = isUrl ? voiceMemo.data : `data:${voiceMemo.mimeType || 'audio/mp4'};base64,${voiceMemo.data}`;
                    const audio = new Audio(src);
                    audio.play().catch(() => {
                      if (!isUrl) {
                        // Try fallback format for legacy base64
                        const fallbackMime = (voiceMemo.mimeType || '').includes('webm') ? 'audio/mp4' : 'audio/webm';
                        const audio2 = new Audio(`data:${fallbackMime};base64,${voiceMemo.data}`);
                        audio2.play();
                      }
                    });
                  }}
                  className="w-10 h-10 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                  title="Play preview"
                >
                  <Play className="w-5 h-5 text-white ml-0.5" />
                </button>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Voice Memo Ready</span>
                  <p className="text-xs text-blue-500 dark:text-blue-400">{formatDuration(voiceMemo.duration)} • Click play to preview</p>
                </div>
                <button 
                  onClick={() => setVoiceMemo(null)} 
                  className="text-blue-400 hover:text-red-500 p-1.5 hover:bg-red-50 dark:hover:bg-red-500/15 rounded-full transition-colors"
                  title="Remove voice memo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

              {/* Selected Images Preview */}
              {selectedImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img 
                        src={img.url} 
                        alt={`Selected ${index + 1}`} 
                        className="w-20 h-20 object-cover rounded-lg border border-slate-200 dark:border-white/10"
                      />
                      <button
                        onClick={() => setSelectedImages(prev => prev.filter((_, i) => i !== index))}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white shadow-md hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="flex flex-col gap-2 mt-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-[#1e1f22] rounded-lg border border-slate-200 dark:border-white/10">
                      <div className="w-8 h-8 rounded bg-slate-200 dark:bg-[#3e4145] flex items-center justify-center text-slate-500 dark:text-slate-400 flex-shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{file.filename}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">{(file.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button
                        onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                        className="text-slate-400 dark:text-slate-500 hover:text-red-500 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Hidden file input for image upload */}
              <input
                ref={imageInputRef}
                type="file"
                accept={IMAGE_ACCEPT}
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />

              {/* Hidden file input for file attachments */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp3,.m4a,.wav,.ogg,.aac,.flac"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Hidden file input for video upload */}
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv"
                onChange={handleVideoSelect}
                className="hidden"
              />

              {/* Video preview */}
              {selectedVideo && (
                <div className="mt-2 relative inline-block">
                  <div className="relative rounded-lg overflow-hidden max-w-[200px]">
                    {selectedVideo.thumbnail_url ? (
                      <img src={selectedVideo.thumbnail_url} alt="Video thumbnail" className="w-full h-auto rounded-lg" />
                    ) : (
                      <div className="w-[200px] h-[120px] bg-slate-900 rounded-lg flex items-center justify-center">
                        <Play className="w-8 h-8 text-white/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-black/50 flex items-center justify-center">
                        <Play className="w-4 h-4 text-white ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded">
                      {Math.floor((selectedVideo.duration || 0) / 60)}:{String(Math.floor((selectedVideo.duration || 0) % 60)).padStart(2, '0')}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="absolute -top-2 -right-2 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm"
                    data-testid="remove-video-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Poll Creator UI */}
              {showPollCreator && (
                <div className={isStudio ? 'st-pollmake' : "mt-3 p-3 bg-slate-50 dark:bg-[#1e1f22] rounded-lg border border-slate-200 dark:border-white/10"}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                      Create Poll
                    </span>
                    <button onClick={() => setShowPollCreator(false)} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    placeholder="Ask a question..."
                    value={pollQuestion}
                    onChange={(e) => setPollQuestion(e.target.value)}
                    className="mb-2"
                    data-testid="poll-question-input"
                  />
                  <div className="space-y-2 mb-3">
                    {pollOptions.map((option, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...pollOptions];
                            newOptions[index] = e.target.value;
                            setPollOptions(newOptions);
                          }}
                          className="flex-1"
                          data-testid={`poll-option-${index}`}
                        />
                        {pollOptions.length > 2 && (
                          <button
                            onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== index))}
                            className="text-slate-400 dark:text-slate-500 hover:text-red-500 px-2"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {pollOptions.length < 5 && (
                        <button
                          onClick={() => setPollOptions([...pollOptions, ''])}
                          className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-600 font-medium"
                        >
                          + Add option
                        </button>
                      )}
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pollAllowOther}
                          onChange={(e) => setPollAllowOther(e.target.checked)}
                          className="rounded border-slate-300 dark:border-white/15"
                          data-testid="poll-allow-other"
                        />
                        Allow &quot;Other&quot; responses
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pollAllowMultiple}
                          onChange={(e) => setPollAllowMultiple(e.target.checked)}
                          className="rounded border-slate-300 dark:border-white/15"
                          data-testid="poll-allow-multiple"
                        />
                        Allow multiple selections
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Group Tags */}
              {selectedGroups.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="selected-group-tags">
                  {selectedGroups.map(g => {
                    const color = g.color || '#6366F1';
                    return (
                      <span
                        key={g.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color: `color-mix(in srgb, ${color} 70%, var(--badge-text-mix))`, border: `1px solid color-mix(in srgb, ${color} 20%, transparent)` }}
                      >
                        @{g.tag}
                        <button onClick={() => handleRemoveGroup(g.id)} className="hover:opacity-60 rounded-full p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  <span className="text-xs text-slate-400 dark:text-slate-500 self-center ml-1">Only these groups will see this post</span>
                </div>
              )}

              <div className={isStudio ? 'st-cmp-tools' : 'flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-white/10'}>
                <TooltipProvider delayDuration={isStudio ? 0 : 300}>
                <div className={isStudio ? 'st-cmp-left' : 'flex items-center gap-1'}>
                  {/* Group Mention Button - available to all users (regular
                      members are limited server-side to groups they belong to). */}
                  {(
                    <div className={isStudio ? 'relative st-aud' : 'relative'} ref={groupPickerRef}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            data-testid="group-mention-btn"
                            variant="ghost" 
                            size="sm" 
                            className={isStudio
                              ? `st-chip${showGroupPicker ? ' st-on' : ''}`
                              : `text-slate-500 dark:text-slate-400 h-8 px-2 ${showGroupPicker ? 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' : ''}`}
                            onClick={(e) => {
                              // Flip downward when the composer sits near the top of the viewport
                              setGroupPickerOpensDown(e.currentTarget.getBoundingClientRect().top < 340);
                              setShowGroupPicker(!showGroupPicker);
                            }}
                          >
                            {isStudio ? <><Users className="w-3.5 h-3.5" />{selectedGroups.length ? `${selectedGroups.length} group${selectedGroups.length > 1 ? 's' : ''}` : 'Everyone'}<ChevronDown className="w-3.5 h-3.5" /></> : <Tag className="w-4 h-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>Groups</p></TooltipContent>
                      </Tooltip>
                      {showGroupPicker && (
                        <div className={`absolute ${groupPickerOpensDown ? 'top-full mt-1' : 'bottom-full mb-1'} left-0 z-50 ${isStudio ? 'st-gpick' : 'w-64 bg-card rounded-lg shadow-lg border border-slate-200 dark:border-white/10'}`} data-testid="group-picker-dropdown">
                          <div className="p-2 border-b border-slate-100 dark:border-white/10">
                            <input
                              type="text"
                              value={groupSearchQuery}
                              onChange={(e) => setGroupSearchQuery(e.target.value)}
                              placeholder="Search groups..."
                              className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-200 dark:border-white/10 focus:outline-none focus:border-blue-300"
                              autoFocus
                              data-testid="group-search-input"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {groupSuggestions.length === 0 ? (
                              <p className="text-center text-xs text-slate-400 dark:text-slate-500 py-3">
                                {groupSearchQuery ? 'No matching groups' : 'No groups yet'}
                              </p>
                            ) : (
                              groupSuggestions.map(g => (
                                <button
                                  key={g.id}
                                  onClick={() => handleSelectGroup(g)}
                                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-white/5 text-left transition-colors"
                                  data-testid={`group-option-${g.tag}`}
                                >
                                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: g.color }}>
                                    {g.name.charAt(0)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{g.name}</div>
                                    <div className="text-xs text-slate-400 dark:text-slate-500">@{g.tag} · {g.member_count} members</div>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {isStudio ? (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="st-tool st-t-photo" data-testid="post-attach-btn"
                                  onClick={() => imageInputRef.current?.click()}
                                  disabled={uploadingImages || selectedImages.length >= 10}><ImageIcon className="w-4 h-4" /><span>Photo</span></button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>Photo</p></TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="st-tool st-t-video" onClick={() => videoInputRef.current?.click()}
                                  disabled={videoUploadProgress !== null || !!selectedVideo}><Video className="w-4 h-4" /><span>Video</span></button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>Video</p></TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="st-tool st-t-file" onClick={() => fileInputRef.current?.click()}
                                  disabled={uploadingFiles || selectedFiles.length >= 5}><FileText className="w-4 h-4" /><span>File</span></button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>File</p></TooltipContent>
                      </Tooltip>
                    </>
                  ) : (
                  <AttachmentMenu
                    testIdPrefix="post"
                    size="sm"
                    align="start"
                    triggerClassName="h-8 px-2"
                    onImageClick={() => imageInputRef.current?.click()}
                    onVideoClick={() => videoInputRef.current?.click()}
                    onFileClick={() => fileInputRef.current?.click()}
                    imageDisabled={uploadingImages || selectedImages.length >= 10}
                    videoDisabled={videoUploadProgress !== null || !!selectedVideo}
                    fileDisabled={uploadingFiles || selectedFiles.length >= 5}
                    uploadProgress={
                      imageUploadProgress !== null ? imageUploadProgress
                      : videoUploadProgress !== null ? videoUploadProgress
                      : fileUploadProgress !== null ? fileUploadProgress
                      : null
                    }
                    activeUploadLabel={
                      videoUploadProgress !== null ? 'video'
                      : imageUploadProgress !== null ? 'image'
                      : fileUploadProgress !== null ? 'file'
                      : ''
                    }
                  />
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        data-testid="gif-btn"
                        variant="ghost" 
                        size="sm" 
                        className={isStudio ? 'st-tool st-t-gif' : 'text-slate-500 dark:text-slate-400 h-8 px-2'}
                        onClick={() => setShowGifPicker(true)}
                      >
                        <Smile className="w-4 h-4" />{isStudio && <span>GIF</span>}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>GIF</p></TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <VoiceMemoButton 
                        onVoiceMemo={(data, duration, mimeType) => setVoiceMemo({ data, duration, mimeType: mimeType || 'audio/mp4' })} 
                        disabled={!!voiceMemo}
                        userId={user.id}
                        label={isStudio ? 'Voice' : undefined}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>Voice Memo</p></TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        data-testid="poll-btn"
                        variant="ghost" 
                        size="sm" 
                        className={isStudio
                          ? `st-tool${showPollCreator ? ' st-on' : ''}`
                          : `text-slate-500 dark:text-slate-400 h-8 px-2 ${showPollCreator ? 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' : ''}`}
                        onClick={() => setShowPollCreator(!showPollCreator)}
                      >
                        <BarChart3 className="w-4 h-4" />{isStudio && <span>Poll</span>}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>Poll</p></TooltipContent>
                  </Tooltip>
                </div>
                </TooltipProvider>
                
                {/* Post Button - Different layouts for mobile vs desktop */}
                {isAdmin ? (
                  <>
                    {/* Desktop: Split button with popover */}
                    <div className={isStudio ? 'st-cmp-right hidden sm:flex items-center' : 'hidden sm:flex items-center'}>
                      {isStudio && (
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="st-tool st-ic"
                                disabled={!hasValidContent}
                                onClick={() => setShowScheduleModal(true)}
                                aria-label="Schedule"
                              >
                                <Clock className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom"><p>Schedule</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <Button 
                        data-testid="post-submit-btn"
                        onClick={handleCreatePost} 
                        disabled={posting || !hasValidContent}
                        className={isStudio ? 'st-postbtn' : 'bg-blue-500 hover:bg-blue-600 h-8 rounded-r-none border-r border-blue-400'}
                        size="sm"
                      >
                        {!isStudio && <Send className="w-4 h-4 mr-1" />}
                        Post
                      </Button>
                      <Popover open={showPostMenu} onOpenChange={setShowPostMenu}>
                        <PopoverTrigger asChild>
                          <Button
                            data-testid="post-options-btn"
                            disabled={posting}
                            variant={isStudio ? 'ghost' : undefined}
                            className={isStudio ? 'st-tool st-ic' : 'bg-blue-500 hover:bg-blue-600 h-8 px-2 rounded-l-none'}
                            size="sm"
                            aria-label={isStudio ? 'More post options' : undefined}
                          >
                            {isStudio ? <MoreHorizontal className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className={isStudio ? 'st-fdopts' : "w-48 p-1"} align="end">
                          <button
                            onClick={() => {
                              setShowPostMenu(false);
                              handleCreatePost();
                            }}
                            disabled={posting || !hasValidContent}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-50"
                          >
                            <Send className="w-4 h-4" /> Post Now
                          </button>
                          <button
                            onClick={() => {
                              setShowPostMenu(false);
                              setShowScheduleModal(true);
                            }}
                            disabled={!hasValidContent}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-50"
                            data-testid="schedule-post-btn"
                          >
                            <CalendarClock className="w-4 h-4" /> Schedule
                          </button>
                          {scheduledPostsCount > 0 && (
                            <>
                              <div className="border-t border-slate-100 dark:border-white/10 my-1" />
                              <button
                                onClick={() => {
                                  setShowPostMenu(false);
                                  setShowScheduledPosts(true);
                                }}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 dark:hover:bg-white/10"
                                data-testid="view-scheduled-btn"
                              >
                                <span className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" /> Scheduled
                                </span>
                                <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 text-xs px-1.5 py-0.5 rounded-full font-medium">
                                  {scheduledPostsCount}
                                </span>
                              </button>
                            </>
                          )}
                          <div className="border-t border-slate-100 dark:border-white/10 my-1" />
                          <button
                            onClick={() => {
                              setShowPostMenu(false);
                              setShowPostAndEmailDialog(true);
                            }}
                            disabled={!hasValidContent}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-50"
                            data-testid="post-and-email-btn"
                          >
                            <Mail className="w-4 h-4" /> Post & Email Team
                          </button>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Mobile: Single button with bottom sheet */}
                    <Button 
                      data-testid="post-mobile-btn"
                      onClick={() => setShowMobilePostMenu(true)}
                      disabled={posting}
                      className="sm:hidden bg-blue-500 hover:bg-blue-600 h-8"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Post
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </>
                ) : (
                  <Button 
                    data-testid="post-submit-btn"
                    onClick={handleCreatePost} 
                    disabled={posting || !hasValidContent}
                    className="bg-blue-500 hover:bg-blue-600 h-8"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Post
                  </Button>
                )}
              </div>
            </div>
        </CardContent>
      </Card>

      {/* Mobile Post Menu Bottom Sheet */}
      <Sheet open={showMobilePostMenu} onOpenChange={setShowMobilePostMenu}>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader className="pb-4">
            <SheetTitle>Post Options</SheetTitle>
          </SheetHeader>
          <div className="space-y-2 pb-4">
            <Button
              variant="outline"
              className="w-full justify-start h-14 rounded-xl text-left"
              onClick={() => {
                setShowMobilePostMenu(false);
                handleCreatePost();
              }}
              disabled={posting || !hasValidContent}
            >
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mr-3">
                <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="font-medium">Post Now</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Share immediately with your team</div>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start h-14 rounded-xl text-left"
              onClick={() => {
                setShowMobilePostMenu(false);
                setShowScheduleModal(true);
              }}
              disabled={!hasValidContent}
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mr-3">
                <CalendarClock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <div className="font-medium">Schedule</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Pick a date and time to post</div>
              </div>
            </Button>
            
            {scheduledPostsCount > 0 && (
              <Button
                variant="outline"
                className="w-full justify-between h-14 rounded-xl"
                onClick={() => {
                  setShowMobilePostMenu(false);
                  setShowScheduledPosts(true);
                }}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mr-3">
                    <Clock className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Scheduled Posts</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">View and manage scheduled posts</div>
                  </div>
                </div>
                <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 text-sm px-2 py-1 rounded-full font-medium">
                  {scheduledPostsCount}
                </span>
              </Button>
            )}
            
            <Button
              variant="outline"
              className="w-full justify-start h-14 rounded-xl text-left"
              onClick={() => {
                setShowMobilePostMenu(false);
                setShowPostAndEmailDialog(true);
              }}
              disabled={!hasValidContent}
            >
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mr-3">
                <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <div className="font-medium">Post & Email Team</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Post and email all team members</div>
              </div>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Post & Email Confirmation Dialog */}
      <PostAndEmailDialog
        isOpen={showPostAndEmailDialog}
        onClose={() => setShowPostAndEmailDialog(false)}
        onPostOnly={() => {
          setShowPostAndEmailDialog(false);
          handleCreatePost();
        }}
        onPostAndEmail={handlePostAndEmail}
        isLoading={postAndEmailLoading}
        selectedGroups={selectedGroups}
      />

      {/* Schedule Post Modal */}
      <SchedulePostModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        user={user}
        onSchedule={handleSchedulePost}
      />

      {/* Scheduled Posts Panel */}
      <ScheduledPostsPanel
        isOpen={showScheduledPosts}
        onClose={() => {
          setShowScheduledPosts(false);
          // Refresh count
          fetch(`${API_URL}/api/comms/v3/scheduled-posts?user_id=${user.id}&church_id=${user.church_id}`)
            .then(res => res.ok ? res.json() : { scheduled_posts: [] })
            .then(data => setScheduledPostsCount(data.scheduled_posts?.length || 0))
            .catch(() => {});
        }}
        user={user}
      />

      {/* Posts Feed */}
      <div className="stagger-children space-y-4">
        {posts.map((post) => (
          <div key={post.id} ref={post.id === highlightedPostId ? highlightRef : null} data-post-id={post.id} className="scroll-mt-20">
            <PostCard 
              post={post} 
              user={user} 
              onUpdate={onRefresh}
              isHighlighted={post.id === highlightedPostId}
              highlightedCommentId={highlightedCommentId}
              autoExpandComments={!!highlightedCommentId && post.id === highlightedPostId}
            />
          </div>
        ))}
        {posts.length === 0 && (
          feedError ? (
            <div className={isStudio ? 'st-fderr' : "text-center py-12 text-slate-500 dark:text-slate-400"} data-testid="feed-error-state">
              {isStudio
                ? <span className="st-fdglyph" aria-hidden="true"><WifiOff /></span>
                : <WifiOff className="w-12 h-12 mx-auto mb-4 opacity-40 text-rose-400" />}
              <p className={isStudio ? undefined : "font-medium text-slate-700 dark:text-slate-300"}>Couldn&apos;t load the feed</p>
              <p className={isStudio ? undefined : "text-sm mt-1"}>Something went wrong reaching the server. Please try again.</p>
              <Button
                variant="outline"
                className={isStudio ? 'st-btn st-sec st-sm' : "mt-4"}
                onClick={onRefresh}
                data-testid="feed-error-retry-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : (
            <div className={isStudio ? 'st-fdempty' : "text-center py-12 text-slate-500 dark:text-slate-400"} data-testid="feed-empty-state">
              {isStudio
                ? <span className="st-fdglyph" aria-hidden="true"><MessageSquare /></span>
                : <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />}
              <p>No posts yet. Be the first to share something!</p>
            </div>
          )
        )}
        
        {/* Infinite scroll trigger */}
        {posts.length >= 20 && (
          <div ref={loadMoreRef} className={isStudio ? 'st-fdmore' : "py-4 text-center"}>
            {loadingMore ? (
              <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading more posts...</span>
              </div>
            ) : hasMore ? (
              <div className="text-slate-400 dark:text-slate-500 text-sm">Scroll for more</div>
            ) : (
              <div className="text-slate-400 dark:text-slate-500 text-sm">You&apos;ve reached the end</div>
            )}
          </div>
        )}
      </div>

      {/* GIF Picker Dialog */}
      <GifPickerDialog 
        open={showGifPicker} 
        onOpenChange={setShowGifPicker}
        onSelect={(gif) => { setSelectedGif(gif); setShowGifPicker(false); }}
      />
    </div>
  );
}

export default FeedView;
